// Third Party Imports
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {CSSTransition} from 'react-transition-group';

// Artifactory Imports
import {SVGIcon} from '@homeaway/react-svg';

// Primary Imports
import {
    MESSAGE_TYPE,
    ANIMATION_TYPE,
    TRANSITION_STATE,
    SETTINGS,
    inlineMessageUtil
} from './utils/inlineMessageUtil';

// styles
import './InlineMessage.less';

class InlineMessage extends Component {
    static propTypes = {
        /** The message's id, required for a11y */
        id: PropTypes.string.isRequired,
        /** The type of message */
        type: PropTypes.oneOf([
            'help',
            'info',
            'warn',
            'danger',
            'success'
        ]),
        /** The content of the message */
        message: PropTypes.node,
        /** Max line count for expand/collapse transition. This is used to transition the max-height
         * css property of the component when entering or leaving the collapsed (aka 'none') state.
         * Setting this value to 0 makes this height transition immediate, and will accomodate
         * a message with any number of lines. When greater than 0, this value should always be
         * greater than or equal to the actual line count. The default value of 3, works for 1,
         * 2, and 3 lines messages with the only minor difference being the effective transition
         * duration and delay per line. Choose 6 if you have 4-6 lines, and if you have a message longer
         * than 6 lines, you probably need something other than the inline message. */
        maxLineCount: PropTypes.oneOf([0, 3, 6]),
        /** Should the message shake. */
        shake: PropTypes.bool,
        /** Shake timeout callback. This callback is executed to let the consuming component
         * know when a shake has completed (used so the consuming component can update
         * its state to set the shake prop back to false). */
        shakeTimeoutCallback: PropTypes.func,
        /** The top margin. This is used to write the inline-style to enable the
         * transistion of this value (if maxLineCount > 0)*/
        marginTop: PropTypes.number,
        /** The bottom margin. This is used to write the inline-style to enable the
         * transistion of this value (if maxLineCount > 0)*/
        marginBottom: PropTypes.number,
        /** Flag to make message text use white text for better a11y contrast when used on dark backgrounds */
        inverse: PropTypes.bool,
        /** The aria-live value for the alert.
         * 'polite' - indicates that the screen reader should wait until the user is idle before presenting updates to the user
         * 'assertive' may interupt user's flow
         */
        ariaLive: PropTypes.oneOf(['assertive', 'polite']),
        /** flag to animate if the type is the same but the message is different */
        animateIfJustMessageChanges: PropTypes.bool,
        /** flag to determine if the shake can be retriggered while shaking */
        canRetriggerShakeWhileShaking: PropTypes.bool,
        /** flag to disable all animation */
        noAnimation: PropTypes.bool
    }

    static defaultProps = {
        type: 'help',
        message: '',
        maxLineCount: 3,
        shake: false,
        marginTop: 0,
        marginBottom: 0,
        inverse: false,
        ariaLive: 'polite',
        animateIfJustMessageChanges: true,
        canRetriggerShakeWhileShaking: true,
        noAnimation: false
    }

    constructor(props) {
        super(props);

        const {
            type,
            message,
            shake,
            marginTop,
            marginBottom,
            maxLineCount,
            noAnimation
        } = this.props;

        const adjType = inlineMessageUtil.getAdjustedType(message, type);
        const transitionState = adjType === MESSAGE_TYPE.NONE
            ? TRANSITION_STATE.EXITED
            : TRANSITION_STATE.ENTERED;

        this.state = {
            type: adjType,
            prevType: adjType,
            message,
            prevMessage: message,
            transitionState,
            animating: false,
            animationReset: false,
            shakeReset: false,
            shake
        };

        // hash of timeouts if needed for cancelling
        this.timeouts = {};

        // since this is based on props, it can be computed now
        this.animationType = inlineMessageUtil.computeAnimationType({
            type: adjType,
            prevType: adjType
        });

        // since this is based on props, it can be computed now
        this.enterExitStyles = inlineMessageUtil.generateEnterExitStyles({
            mTop: marginTop,
            mBottom: marginBottom,
            maxLineCount,
            noAnimation
        });

        // CSSTransition state change callbacks
        this.onEnter = this.onTransitionStateChange.bind(this, TRANSITION_STATE.ENTER);
        this.onEntering = this.onTransitionStateChange.bind(this, TRANSITION_STATE.ENTERING);
        this.onEntered = this.onTransitionStateChange.bind(this, TRANSITION_STATE.ENTERED);
        this.onExit = this.onTransitionStateChange.bind(this, TRANSITION_STATE.EXIT);
        this.onExiting = this.onTransitionStateChange.bind(this, TRANSITION_STATE.EXITING);
        this.onExited = this.onTransitionStateChange.bind(this, TRANSITION_STATE.EXITED);
    }

    UNSAFE_componentWillReceiveProps(nextProps) { // eslint-disable-line camelcase
        const {
            type,
            message,
            shake,
            shakeReset: shakeCurrentlyReset
        } = this.state;

        const {
            type: nextType,
            message: newMessage,
            shake: newShake,
            maxLineCount,
            marginTop,
            marginBottom,
            animateIfJustMessageChanges,
            canRetriggerShakeWhileShaking,
            noAnimation
        } = nextProps;

        // variables to determine if we need to update state based on a change of:
        // message, type, animation, shake, animation reset, or shake reset
        // (resets are a render frame in which the shake or animate classes are removed so
        // they can be re-added on the next render frame to re-trigger a shake or animation)

        // if an animation timeout exist, we are in the process of animating
        const alreadyAnimating = !!this.timeouts[ANIMATION_TYPE.TRANSITION];
        // if an shake timeout exist, we are in the process of animating
        const alreadyShaking = !!this.timeouts[ANIMATION_TYPE.SHAKE];
        // checks to get type NONE if the newMessage falsey
        const newType = inlineMessageUtil.getAdjustedType(newMessage, nextType);
        const messageChanged = newMessage !== message;
        // we should animate if the type has changed, or if the message
        // has changed and that is set to trigger and animation
        const animating = newType !== type || (messageChanged && animateIfJustMessageChanges);
        // if we should animate and we are already animating, we need to perform an animation reset
        const animationReset = alreadyAnimating && animating;
        // if we should shake and we are already shaking, we need to perform a shake reset
        const shakeReset = alreadyShaking && !!newShake;
        // some additional conditions that determine if we should shake (TODO: explain better)
        const shouldShake = (newShake && !shake) || (newShake && shakeCurrentlyReset);
        // some additional conditions that determine if we should animate (TODO: explain better)
        const shouldAnimate = !shouldShake && (animating || (alreadyAnimating && !shakeReset));

        // object with properties indicating the type of animation (noneToIcon, iconToHelp, etc)
        // since this is based on props, it can be computed now
        this.animationType = inlineMessageUtil.computeAnimationType({
            type: newType,
            prevType: type
        });

        // object with properties containing the transition styles for the CSS Transition for the root element
        // since this is based on props, it can be computed now
        this.enterExitStyles = inlineMessageUtil.generateEnterExitStyles({
            mTop: marginTop,
            mBottom: marginBottom,
            maxLineCount,
            noAnimation
        });

        // are the appropriate conditions meet that require and update of state
        const updatedState = [
            messageChanged,
            animating,
            shouldShake,
            (shakeReset && canRetriggerShakeWhileShaking)
        ].some((condition) => (!!condition));

        if (updatedState) {
            this.setState({
                prevType: type,
                type: newType,
                prevMessage: message,
                message: newMessage,
                animating: shouldAnimate,
                shake: !!newShake && !shouldAnimate,
                animationReset,
                shakeReset
            }, () => {
                if (shouldAnimate) {
                    // if we should animating (i.e. animate icon and possibly message) add the
                    // animation timeout (that will clear and delete any existing animation timeout)
                    this.addAnimationTimeout();
                    if (alreadyShaking && this.props.shakeTimeoutCallback) {
                        // if we are shaking, our state update will stop that, but we still need to clear
                        // and delete any existing shake timeouts and call the shake timout callback
                        this.clearAndDeleteTimeouts(ANIMATION_TYPE.SHAKE);
                        this.props.shakeTimeoutCallback();
                    }
                } else if (shouldShake || (shakeReset && canRetriggerShakeWhileShaking)) {
                    // if we should shake or retrigger a shake, add the shake timeout
                    // and clear and delete any existing animation timeout
                    this.addShakeTimeout();
                    this.clearAndDeleteTimeouts(ANIMATION_TYPE.TRANSITION);
                }
            });
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        const {shake: newShake, canRetriggerShakeWhileShaking} = nextProps;
        const {shakeReset: shakeCurrentlyReset, shake} = this.state;
        const shouldShake = (newShake && !shake) || (newShake && shakeCurrentlyReset);
        const alreadyShaking = !!this.timeouts[ANIMATION_TYPE.SHAKE];
        const shakeReset = alreadyShaking && !!newShake;
        const shouldUpdate = [
            !(
                nextProps.message === this.props.message
                && nextProps.type === this.props.type
                && (typeof this.timeouts[ANIMATION_TYPE.TRANSITION] !== 'undefined' || typeof this.timeouts[ANIMATION_TYPE.SHAKE] !== 'undefined')
                && this.state.transitionState === nextState.transitionState
            ),
            (nextState.animationReset !== this.state.animationReset),
            (nextState.shakeReset !== this.state.shakeReset),
            (shouldShake || (shakeReset && canRetriggerShakeWhileShaking)),
            (this.props.shake && !newShake)
        ].some((condition) => (!!condition));

        return shouldUpdate;
    }

    componentDidUpdate() {
        if (this.state.animationReset || this.state.shakeReset) {
            this.setState({
                animationReset: false,
                shakeReset: false,
            });
        }
    }

    onTransitionStateChange(transitionState) {
        if (this.state.transitionState !== transitionState) {
            this.setState({transitionState});
        }
    }

    clearAndDeleteTimeouts(type) {
        clearTimeout(this.timeouts[type]);
        delete this.timeouts[type];
    }

    addAnimationTimeout = () => {
        const {noAnimation} = this.props;
        this.clearAndDeleteTimeouts(ANIMATION_TYPE.TRANSITION);
        this.timeouts[ANIMATION_TYPE.TRANSITION] = setTimeout(() => {
            delete this.timeouts[ANIMATION_TYPE.TRANSITION];
            this.setState({
                animating: false,
                animationReset: false,
            });
        }, noAnimation ? 0 : SETTINGS.TRANSITION_DURATION);
    }

    addShakeTimeout = () => {
        const {shakeTimeoutCallback} = this.props;
        this.clearAndDeleteTimeouts(ANIMATION_TYPE.SHAKE);
        this.timeouts[ANIMATION_TYPE.SHAKE] = setTimeout(() => {
            delete this.timeouts[ANIMATION_TYPE.SHAKE];
            this.setState({
                shake: false,
                shakeReset: false,
            }, () => {
                if (shakeTimeoutCallback) {
                    shakeTimeoutCallback();
                }
            });
        }, SETTINGS.SHAKE_DURATION);
    }

    getClassNames(prefix) {
        const {
            type,
            prevType,
            animating,
            animationReset
        } = this.state;

        const {noAnimation} = this.props;

        const {
            iconToIcon,
            iconToHelp,
            helpToIcon,
            iconToNone,
            noneToIcon,
            anyToNone,
            noneToAny
        } = this.animationType;

        const isAnimating = animating && !animationReset;

        return classnames('', {
            [`${prefix}transition-reset`]: animationReset,
            [`${prefix}${type}`]: type,
            [`${prefix}${prevType}-to-${type}`]: isAnimating,
            [`${prefix}icon-to-icon`]: isAnimating && iconToIcon, // icon to icon
            [`${prefix}icon-to-help`]: isAnimating && iconToHelp, // icon to help
            [`${prefix}help-to-icon`]: isAnimating && helpToIcon, // help to icon
            [`${prefix}icon-to-none`]: isAnimating && iconToNone, // icon to none
            [`${prefix}none-to-icon`]: isAnimating && noneToIcon, // none to icon
            [`${prefix}any-to-none`]: isAnimating && anyToNone, // any to none
            [`${prefix}none-to-any`]: isAnimating && noneToAny, // none to any
            [`${prefix}no-animation`]: noAnimation, // no animation
        });
    }

    render() {
        const {
            message,
            prevMessage,
            type,
            prevType,
            animating,
            shake,
            transitionState,
            animationReset,
            shakeReset
        } = this.state;

        const {
            inverse,
            noAnimation,
            id,
            className,
            ariaLive
        } = this.props;

        // if animating out (i.e. going to no message), still show the previous message
        const messageToShow = animating && type === MESSAGE_TYPE.NONE ? prevMessage : message;
        // if animating out (i.e. going to no message), still show the previous type
        const typeToUse = inlineMessageUtil.getIconTypeToUse({type, prevType, animating});
        const messageChanged = prevMessage !== messageToShow;
        const iconMarkup = inlineMessageUtil.getIconMarkup({type, prevType, animating});
        const enterExitStyles = this.enterExitStyles[transitionState] || {};

        const rootClassNames = classnames('InlineMessage', this.getClassNames('InlineMessage--'), {
            [className]: !!className
        });
        const shakeWrapperClassNames = classnames('InlineMessage__shake-wrapper', {
            'InlineMessage__shake-wrapper--shake': shake && !shakeReset,
            'text-white': inverse
        });
        const iconClassNames = classnames('InlineMessage__icon', 'InlineMessage--small-text', 'small', this.getClassNames('InlineMessage__icon--'), {
            'text-info': typeToUse === MESSAGE_TYPE.INFO && !inverse,
            'text-success': typeToUse === MESSAGE_TYPE.SUCCESS && !inverse,
            'text-danger': typeToUse === MESSAGE_TYPE.ERROR && !inverse
        });
        const messageClassNames = classnames('InlineMessage__message', 'InlineMessage--small-text', 'small', this.getClassNames('InlineMessage__message--'), {
            'InlineMessage__message--same-message': !messageChanged && type !== MESSAGE_TYPE.NONE
        });

        const typeRequiresAriaLive = [
            type === MESSAGE_TYPE.ERROR,
            type === MESSAGE_TYPE.SUCCESS,
            type === MESSAGE_TYPE.INFO,
            type === MESSAGE_TYPE.WARN
        ].some((condition) => (!!condition));

        const messageToShowIsString = typeof messageToShow === 'string';
        const messageProps = {
            id,
            key: `message-${messageToShowIsString ? 'string' : 'node'}-${animationReset ? '0' : '1'}`,
            className: messageClassNames,
            ...(typeRequiresAriaLive && messageToShow !== '' && {role: 'alert', 'aria-live': ariaLive})
        };

        return (
            <CSSTransition
                classNames="InlineMessage-CSSTransition"
                timeout={noAnimation ? 0 : SETTINGS.TRANSITION_DURATION}
                in={type !== MESSAGE_TYPE.NONE}
                mountOnEnter
                unmountOnExit
                onEnter={this.onEnter}
                onEntering={this.onEntering}
                onEntered={this.onEntered}
                onExit={this.onExit}
                onExiting={this.onExiting}
                onExited={this.onExited}
            >

                <div className={rootClassNames} style={enterExitStyles}>
                    <div className={shakeWrapperClassNames} key={`inline-message__shake-wrapper-${shakeReset ? '0' : '1'}`}>
                        {iconMarkup &&
                            <SVGIcon className={iconClassNames} markup={iconMarkup} key={`icon-${animationReset ? '0' : '1'}`}/>
                        }
                        {messageToShow && messageToShowIsString &&
                            <span
                                {...messageProps}
                                dangerouslySetInnerHTML={{__html: messageToShow}}
                            />
                        }
                        {messageToShow && !messageToShowIsString &&
                            <div {...messageProps}>
                                {messageToShow}
                            </div>
                        }
                    </div>
                </div>
            </CSSTransition>
        );
    }
}

export default InlineMessage;
